import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { EquipmentCategories, RentalTeaser, HomePartnerMap } from '../components/HomeEquipment';
import { api, TIMEPAYMENT_URL, assetUrl } from '../lib/api';
import { MachineCard } from './Machines';
import WhyUsMarquee from '../components/WhyUsMarquee';
import YouTubeLatest from '../components/YouTubeLatest';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import AnimatedNumber from '../components/AnimatedNumber';
import { useSiteSettings } from '../context/SiteSettingsContext';
import useSEO from '../hooks/useSEO';
import { useSiteTexts, lines } from '../lib/siteTexts';
import Br from '../components/Br';


const Home = () => {
    const [machines, setMachines] = useState([]);
    const [videos, setVideos] = useState([]);
    const [news, setNews] = useState([]);
    const [projects, setProjects] = useState([]);
    const { settings } = useSiteSettings();
    const t = useSiteTexts();
    const industries = lines(t('home.marquee'));
    const heroVideo = settings?.hero_video_url ? assetUrl(settings.hero_video_url) : '';
    const heroPoster = settings?.hero_video_poster ? assetUrl(settings.hero_video_poster) : '';

    // Editable homepage copy. Falls back to the current defaults when empty.
    const hc = settings?.home_copy || {};
    const copy = {
        hero_eyebrow: hc.hero_eyebrow || 'Authorized Dry Ice Cleaning Equipment Dealer',
        hero_h1_line1: hc.hero_h1_line1 || 'Dry ice cleaning',
        hero_h1_line2: hc.hero_h1_line2 || 'equipment,',
        hero_h1_accent: hc.hero_h1_accent || 'from the experts.',
        hero_sub: hc.hero_sub || 'Shop dry ice blasting machines, nozzles and accessories from a team that has run them in the field for decades. Buy parts online, get a quote on machines, and get trained to use them right.',
        hero_sub_secondary: hc.hero_sub_secondary || 'Nationwide shipping, financing, rentals and an authorized partner network.',
        services_eyebrow: hc.services_eyebrow || '/ 01 — Our services',
        services_heading: hc.services_heading || 'Four cleaning\ntechnologies. One team.',
    };

    // Before/after proof pairs — pulled from Admin → Branding. Falls back to stock photos.
    const DEFAULT_BA = [
        {
            before_image: 'https://images.unsplash.com/photo-1581092162384-8987c1d64926?w=1200&auto=format&fit=crop&q=70',
            after_image: 'https://images.unsplash.com/photo-1565043534407-2df1a4f26fe6?w=1200&auto=format&fit=crop&q=70',
            title: 'Injection mold cleaning',
            description: 'Dry ice blasting — carbon buildup removed, no disassembly.',
        },
        {
            before_image: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1200&auto=format&fit=crop&q=70',
            after_image: 'https://images.unsplash.com/photo-1581091012184-5c8d4a5f3d29?w=1200&auto=format&fit=crop&q=70',
            title: 'Manufacturing equipment',
            description: 'Dry ice blasting — grease and buildup removed without water or chemicals.',
        },
    ];
    const baItems = (Array.isArray(settings?.before_after_items) && settings.before_after_items.length > 0)
        ? settings.before_after_items
        : DEFAULT_BA;

    useSEO({
        title: 'Dry Ice Bulls — Dry Ice Blasting Machines, Accessories, Rentals & Training',
        description: 'Authorized dry ice cleaning equipment dealer. Shop dry ice blasting machines, nozzles and parts, rent equipment, get operator training and find trained partners nationwide.',
    });

    useEffect(() => {
        api.get('/machines', { params: { featured: true } }).then((r) => setMachines(r.data.slice(0, 6))).catch(() => {});
        api.get('/videos', { params: { featured: true } }).then((r) => setVideos(r.data.slice(0, 3))).catch(() => {});
        api.get('/news').then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
        api.get('/projects').then((r) => setProjects(r.data.slice(0, 3))).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-neutral-200 bg-white">
                <div className="absolute inset-0">
                    {heroVideo ? (
                        <video
                            key={heroVideo}
                            src={heroVideo}
                            poster={heroPoster || undefined}
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-cover opacity-85"
                            data-testid="hero-video"
                        />
                    ) : (
                        <img
                            src="https://images.pexels.com/photos/34718922/pexels-photo-34718922.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
                            alt=""
                            fetchPriority="high"
                            decoding="async"
                            className="w-full h-full object-cover opacity-15"
                        />
                    )}
                    {/* Softer overlay so the video reads clearly while keeping copy legible.
                        Left side: near-white so headline stays crisp. Right side: transparent so the video shines through. */}
                    <div className={heroVideo
                        ? 'absolute inset-0 bg-gradient-to-r from-white/80 via-white/25 to-transparent'
                        : 'absolute inset-0 bg-gradient-to-br from-white via-white/80 to-white/40'} />
                </div>

                <div className="relative container-page min-h-[80vh] flex flex-col justify-end pt-32 pb-20">
                    <div className="max-w-5xl fade-up">
                        <div className="flex items-center gap-3 mb-6 label-caps text-brand-primary">
                            <span className="h-px w-10 bg-brand-primary" />
                            {copy.hero_eyebrow}
                        </div>
                        <h1 className="font-display font-black uppercase text-5xl sm:text-6xl md:text-7xl lg:text-[104px] leading-[0.92] text-neutral-900">
                            {copy.hero_h1_line1}<br />
                            {copy.hero_h1_line2} <span className="text-brand-primary">{copy.hero_h1_accent}</span>
                        </h1>
                        <p className="mt-8 max-w-2xl text-lg text-neutral-600 leading-relaxed">
                            {copy.hero_sub}
                            <span className="block mt-2 text-neutral-500 text-base">
                                {copy.hero_sub_secondary}
                            </span>
                        </p>
                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link
                                to="/shop"
                                onClick={() => window.trackConversion && window.trackConversion('shop_click', { location: 'home_hero' })}
                                className="group inline-flex items-center gap-3 bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-7 py-4 label-caps transition-colors"
                                data-testid="hero-cta-equipment"
                            >
                                {t('home.hero.cta1')}
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/contact?interest=quote&subject=Machine+Quote"
                                onClick={() => window.trackConversion && window.trackConversion('equipment_quote_click', { location: 'home_hero' })}
                                className="inline-flex items-center gap-3 border border-neutral-300 hover:border-neutral-900 text-neutral-900 px-7 py-4 label-caps transition-colors"
                                data-testid="hero-cta-quote"
                            >
                                {t('home.hero.cta2')}
                                <ChevronRight className="h-4 w-4" />
                            </Link>
                        </div>

                        {(() => {
                            // Stats block — driven by Admin → Branding → "About stats".
                            // Empty in admin (or fewer than 1 item) hides this block entirely.
                            const stats = Array.isArray(settings?.about_stats) ? settings.about_stats.filter(s => (s?.value || '').trim() || (s?.label || '').trim()) : [];
                            if (stats.length === 0) return null;
                            return (
                                <div className="mt-16 grid grid-cols-3 gap-6 max-w-2xl border-t border-neutral-200 pt-8" data-testid="home-hero-stats">
                                    {stats.slice(0, 3).map((s, i) => (
                                        <div key={`${s.label}-${i}`}>
                                            <div className="font-display text-4xl md:text-5xl font-black text-neutral-900">
                                                <AnimatedNumber value={s.value} />
                                            </div>
                                            <div className="label-caps text-neutral-500 mt-2">{s.label}</div>
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </section>

            {/* MARQUEE — dark industrial accent stripe.
                NOTE: uses inline styles instead of Tailwind classes so the
                site-wide light-theme override in index.css does NOT flip
                the colors. This is an intentional dark band between the
                white hero and the next white section. */}
            <section className="border-y border-neutral-900 py-6 overflow-hidden" style={{ backgroundColor: '#0A0A0A' }}>
                <div className="flex marquee-track whitespace-nowrap">
                    {[...industries, ...industries].map((ind, i) => (
                        <span key={i} className="mx-8 font-display font-black text-2xl md:text-4xl tracking-tight uppercase" style={{ color: 'rgba(255,255,255,0.35)' }}>
                            {ind} <span style={{ color: '#F7941D' }}>/</span>
                        </span>
                    ))}
                </div>
            </section>

            {/* FEATURED MACHINES */}
            <section className="border-t border-neutral-200 bg-neutral-50" data-testid="home-featured-equipment">
                <div className="container-page py-24 md:py-32">
                    <div className="flex items-end justify-between gap-6 mb-16 flex-wrap">
                        <div>
                            <div className="label-caps text-brand-primary mb-4">{t('home.featured.eyebrow')}</div>
                            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl text-neutral-900">
                                <Br text={t('home.featured.heading')} />
                            </h2>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/shop?type=machine" className="label-caps border border-neutral-300 hover:border-neutral-900 text-neutral-900 px-5 py-3 transition-colors" data-testid="home-shop-machines">Machines</Link>
                            <Link to="/shop?type=accessory" className="label-caps border border-neutral-300 hover:border-neutral-900 text-neutral-900 px-5 py-3 transition-colors" data-testid="home-shop-accessories">Accessories & Parts</Link>
                            <Link to="/shop" className="label-caps bg-neutral-900 hover:bg-brand-primary text-on-dark px-5 py-3 inline-flex items-center gap-2 transition-colors" data-testid="home-machines-link">
                                View full shop <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {machines.map((m) => (
                            <MachineCard key={m.id} m={m} />
                        ))}
                    </div>
                </div>
            </section>

            {/* EQUIPMENT CATEGORIES */}
            <EquipmentCategories />

            {/* INDUSTRIES SERVED */}
            <section className="border-t border-neutral-200 bg-neutral-50 py-20 md:py-28">
                <div className="container-page">
                    <div className="flex items-end justify-between gap-6 mb-14 flex-wrap">
                        <div>
                            <div className="label-caps text-brand-primary mb-4">{t('home.ind.eyebrow')}</div>
                            <h2 className="font-display font-black uppercase text-4xl md:text-6xl text-neutral-900 leading-[0.95]">
                                <Br text={t('home.ind.heading')} />
                            </h2>
                        </div>
                        <Link to="/industries" className="label-caps text-neutral-600 hover:text-brand-primary transition-colors inline-flex items-center gap-2" data-testid="home-industries-link">
                            All Industries <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {lines(t('home.ind.list')).map((ind) => (
                            <Link
                                key={ind}
                                to="/industries"
                                className="group border border-neutral-200 hover:border-brand-primary bg-white transition-colors p-5"
                                data-testid={`home-industry-${ind.toLowerCase().replace(/[^a-z]/g, '-')}`}
                            >
                                <div className="label-caps text-neutral-500 group-hover:text-brand-primary transition-colors text-xs mb-2">/ Industry</div>
                                <div className="font-display font-bold uppercase text-lg text-neutral-900 leading-tight">{ind}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE US */}
            <section className="container-page py-24 md:py-28">
                <div className="max-w-4xl mb-14">
                    <div className="label-caps text-brand-primary mb-4">{t('home.why.eyebrow')}</div>
                    <h2 className="font-display font-black uppercase text-4xl md:text-6xl text-neutral-900 leading-[0.95]">
                        <Br text={t('home.why.heading')} /><br /><span className="text-brand-primary">{t('home.why.accent')}</span>
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => ({ n: `0${i}`, t: t(`home.why.${i}.title`), d: t(`home.why.${i}.text`) })).map((p) => (
                        <div key={p.n} className="border border-neutral-200 bg-white p-8 hover:border-brand-primary transition-colors" data-testid={`why-pillar-${p.n}`}>
                            <div className="font-display font-black text-brand-primary text-5xl mb-4 leading-none">{p.n}</div>
                            <div className="font-display font-bold uppercase text-xl text-neutral-900 leading-tight mb-3">{p.t}</div>
                            <p className="text-sm text-neutral-600 leading-relaxed">{p.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* RENTAL TEASER */}
            <RentalTeaser />

            {/* SEE THE DIFFERENCE — BEFORE / AFTER */}
            <section className="container-page py-24 md:py-28">
                <div className="max-w-4xl mb-12">
                    <div className="label-caps text-brand-primary mb-4">{t('home.ba.eyebrow')}</div>
                    <h2 className="font-display font-black uppercase text-4xl md:text-6xl text-neutral-900 leading-[0.95]">
                        <Br text={t('home.ba.heading')} /><br /><span className="text-brand-primary">{t('home.ba.accent')}</span>
                    </h2>
                    <p className="mt-4 max-w-2xl text-base text-neutral-600">
                        {t('home.ba.text')}
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {baItems.slice(0, 4).map((b, i) => (
                        <div key={i} data-testid={`ba-item-${i}`}>
                            <BeforeAfterSlider
                                before={assetUrl(b.before_image)}
                                after={assetUrl(b.after_image)}
                                beforeLabel="Before"
                                afterLabel="After"
                                testid={`ba-slider-${i}`}
                            />
                            {(b.title || b.description) && (
                                <div className="mt-3">
                                    {b.title && (
                                        <div className="font-display font-bold uppercase text-lg text-neutral-900">
                                            {b.title}
                                        </div>
                                    )}
                                    {b.description && (
                                        <div className="text-sm text-neutral-500">{b.description}</div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* RECENT INDUSTRIAL PROJECTS — real case studies (auto-hides when empty) */}
            {projects.length > 0 && (
                <section className="container-page py-20 md:py-24" data-testid="home-recent-projects">
                    <div className="flex items-end justify-between gap-6 mb-12 flex-wrap">
                        <div className="max-w-3xl">
                            <div className="label-caps text-brand-primary mb-4">{t('home.projects.eyebrow')}</div>
                            <h2 className="font-display font-black uppercase text-4xl md:text-6xl text-neutral-900 leading-[0.95]">
                                <Br text={t('home.projects.heading')} /><br /><span className="text-brand-primary">{t('home.projects.accent')}</span>
                            </h2>
                        </div>
                        <Link to="/projects" className="label-caps text-neutral-600 hover:text-brand-primary transition-colors inline-flex items-center gap-2">
                            All Case Studies <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {projects.map((p) => (
                            <Link
                                key={p.id}
                                to={`/projects/${p.slug || p.id}`}
                                className="group border border-neutral-200 bg-white hover:border-brand-primary transition-colors flex flex-col"
                                data-testid={`home-project-${p.slug || p.id}`}
                            >
                                <div className="aspect-[4/3] bg-neutral-100 overflow-hidden relative">
                                    {p.cover_image && <img src={p.cover_image} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                                    <div className="absolute bottom-3 right-3 label-caps text-xs bg-brand-primary text-white px-2.5 py-1 font-bold">
                                        {p.category?.replace(/-/g, ' ')}
                                    </div>
                                </div>
                                <div className="p-5 flex-1 flex flex-col">
                                    <h3 className="font-display font-bold uppercase text-lg text-neutral-900 leading-tight group-hover:text-brand-primary transition-colors">
                                        {p.title}
                                    </h3>
                                    {p.summary && <p className="mt-2 text-sm text-neutral-600 leading-relaxed line-clamp-2 flex-1">{p.summary}</p>}
                                    <div className="mt-4 inline-flex items-center gap-1.5 label-caps text-brand-primary text-xs">
                                        View case study <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* PARTNER MAP */}
            <HomePartnerMap />

            {/* WHY US */}
            <WhyUsMarquee />

            {/* LATEST YOUTUBE */}
            <YouTubeLatest />

            {/* VIDEOS + NEWS SPLIT */}
            <section className="container-page py-24 md:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                    <div>
                        <div className="label-caps text-brand-primary mb-4">{t('home.videos.eyebrow')}</div>
                        <h2 className="font-display font-black uppercase tracking-tighter text-3xl md:text-5xl mb-8">{t('home.videos.heading')}</h2>
                        <div className="space-y-4">
                            {videos.map((v) => (
                                <Link
                                    key={v.id}
                                    to="/videos"
                                    className="group flex items-center gap-5 border border-white/10 hover:border-brand-primary transition-colors p-4"
                                    data-testid={`home-video-${v.id}`}
                                >
                                    <div className="h-20 w-32 flex-shrink-0 bg-brand-surface overflow-hidden">
                                        {v.thumbnail_url ? (
                                            <img src={v.thumbnail_url} alt={v.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                                        ) : null}
                                    </div>
                                    <div>
                                        <div className="font-display font-bold text-white group-hover:text-brand-primary transition-colors">{v.title}</div>
                                        <div className="text-sm text-white/50 line-clamp-1">{v.description}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                        <Link to="/videos" className="mt-6 inline-flex items-center gap-2 label-caps text-white/70 hover:text-white transition-colors">
                            All videos <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <div>
                        <div className="label-caps text-brand-primary mb-4">{t('home.news.eyebrow')}</div>
                        <h2 className="font-display font-black uppercase tracking-tighter text-3xl md:text-5xl mb-8">{t('home.news.heading')}</h2>
                        <div className="space-y-6">
                            {news.map((n) => (
                                <Link
                                    key={n.id}
                                    to={`/news/${n.slug || n.id}`}
                                    className="group block border-b border-white/10 pb-6 hover:border-brand-primary transition-colors"
                                    data-testid={`home-news-${n.id}`}
                                >
                                    <div className="label-caps text-white/40 mb-2">
                                        {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </div>
                                    <h3 className="font-display font-black text-xl md:text-2xl text-white group-hover:text-brand-primary transition-colors">{n.title}</h3>
                                    <p className="text-white/60 mt-2 line-clamp-2">{n.excerpt}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ TEASE */}
            <section className="border-t border-neutral-200 py-16 md:py-20">
                <div className="container-page flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="label-caps text-brand-primary mb-3">{t('home.faq.eyebrow')}</div>
                        <h2 className="font-display font-black uppercase text-2xl md:text-3xl leading-tight text-neutral-900">
                            <Br text={t('home.faq.heading')} /><br className="hidden md:block" />
                            <span className="text-brand-primary">{t('home.faq.accent')}</span>
                        </h2>
                    </div>
                    <Link
                        to="/faq"
                        className="inline-flex items-center gap-2 border border-neutral-900 hover:bg-neutral-900 hover:text-on-dark text-neutral-900 px-6 py-3.5 label-caps transition-colors whitespace-nowrap"
                        data-testid="home-faq-link"
                    >
                        {t('home.faq.button')}
                    </Link>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-white/10 bg-brand-primary">
                <div className="container-page py-16 md:py-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                    <div>
                        <h2 className="font-display font-black uppercase tracking-tighter text-3xl md:text-5xl text-brand-ink">
                            {t('home.cta.heading')}
                        </h2>
                        <p className="text-brand-ink/80 mt-3 max-w-xl">
                            {t('home.cta.text')}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/shop" className="bg-brand-ink text-brand-primary hover:bg-black/80 px-7 py-4 label-caps transition-colors" data-testid="home-bottom-cta-quote">
                            {t('home.cta.button1')}
                        </Link>
                        <a href={TIMEPAYMENT_URL} target="_blank" rel="noreferrer" className="border-2 border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-brand-primary px-7 py-4 label-caps transition-colors" data-testid="home-bottom-cta-finance">
                            {t('home.cta.button2')}
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};


export default Home;
